import { useCallback, useMemo, useState } from 'react';

import {
  amendBooking,
  applyBookingDiscount,
  bookingsKey,
  changeBookingStage,
  changeGarmentState,
  createBooking,
  extendBooking,
  issueBooking,
  recordBookingPayment,
  recordFitting,
  returnBooking,
  settleBookingDeposit,
  useBooking,
  useBookingCascade,
  useLetterhead,
  type ApplyDiscountInput,
  type Booking,
  type BookingGarment,
  type ChangeBookingStageInput,
  type BookingWearer,
  type ChangeGarmentStateInput,
  type DepositRefundPoint,
  type ExtendBookingInput,
  type PaymentKind,
  type PaymentMethod,
  type RecordFittingInput,
} from '@/api/bookings';
import { availabilityKey, useCatalogue } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { Button, PageHeading } from '@/components/PageHeading';
import { WriteAction } from '@/components/WriteAction';
import { cashKey } from '@/api/cash';
import { collectAfterSettling, recordBookingPaymentInParts } from '@core/api/bookingCommands';
import type { SplitPart } from '@core/cash/split';
import { paymentsAfterIssueInParts } from '@core/bookings/handoverMoney';
import { toIsoDate } from '@core/format/dates';
import { can, canAct, hasFeature } from '@core/session/capability';
import { nextStep, type NextStep } from '@core/bookings/nextStep';
import { stageMoveInput, stageMoves, type StageMove } from '@core/bookings/stageMoves';
import { canDiscount } from '@core/bookings/discountForm';
import type { Session } from '@core/session/types';

import { AmendBookingPage } from './bookings/AmendBookingPage';
import { CascadeNotice } from './bookings/CascadeNotice';
import { ExtendBookingPage } from './bookings/ExtendBookingPage';
import { amendmentRequest, canAmend, type Amendment } from '@core/bookings/amendment';
import { canExtend } from '@core/bookings/extension';
import { BookedPage } from './bookings/BookedPage';
import { BookingDetail } from './bookings/BookingDetail';
import { BookingBoard, type BoardIntent } from './bookings/BookingBoard';
import { GarmentPhotos } from './bookings/GarmentPhotos';
import { GarmentTimeline } from './units/GarmentTimeline';
import { FittingDrawer } from './bookings/FittingDrawer';
import { GarmentStateDrawer } from './bookings/GarmentStateDrawer';
import { HoldDrawer } from './bookings/HoldDrawer';
import { IssueCounter } from './bookings/IssueCounter';
import { NewBookingPage, type AvailableUnit } from './bookings/NewBookingPage';
import { NextStepCard } from './bookings/NextStepCard';
import { DiscountDrawer } from './bookings/DiscountDrawer';
import { PaymentPage } from './bookings/PaymentPage';
import { StageDialog } from './bookings/StageDialog';
import { CheckGarments } from './bookings/CheckGarments';
import { SettlePage } from './bookings/SettlePage';
import { inspectionsKey, pendingInspectionsKey } from '@/api/inspections';
import { saveInspection } from '@core/api/inspectionCommands';
import type { toRequest } from '@core/returns/inspection';
import { boardStart, takeBookingIntent } from '@core/bookings/registerViews';
import { markReadyMoves } from '@core/bookings/bookingLists';
import { ReturnCounter } from './bookings/ReturnCounter';
import { bookingDefaultsOf, type BookingDraft } from '@core/bookings/draft';
import {
  createBookingInput,
  paymentsAtBooking,
  railFromCatalogue,
} from '@core/bookings/takeBooking';

/**
 * Bookings (spec Section 8, rebuilt — see `docs/Booking_Flow_Rebuild.md`).
 *
 * <p>
 * <strong>This page owns the queries and the mutations and nothing else.</strong> Every screen it
 * shows — the register, the detail, the two counters, the create drawer — is a presentational
 * component that takes data and hands back an intent. That split is what let those five be built
 * and tested against the contract before any of this existed, and it is why none of them imports
 * `apiFetch`.
 * </p>
 *
 * <p>
 * <strong>Which screen is showing is local state, not a route.</strong> The shell navigates by key
 * through `pages/registry.tsx` and there is no router, so a booking's detail is a selection here
 * rather than a URL. The cost is real and worth stating: a detail screen cannot be linked to or
 * refreshed into. That is the same limitation the units screen carries, and it is one decision —
 * adding a router — rather than five screens each working around it.
 * </p>
 */
export function BookingsPage({
  session,
  initialView = 'all',
  initialFind = '',
  initialBookingId,
}: {
  session: Session;
  /** The saved view to open on — a Today card names one; the sidebar does not. */
  initialView?: string | undefined;
  /** A code the global find resolved, already in the search box when the screen opens. */
  initialFind?: string | undefined;
  /** The booking the global find settled on — a scanned slip — opened straight away. */
  initialBookingId?: number | undefined;
}) {
  const branchId = session.branch?.id;

  // The shop's date, not the browser's. Everything overdue is computed against it server-side, and
  // the register's saved views key on it — a laptop with a wrong clock must not turn a current
  // booking red or file today's pickups under yesterday.
  const [today] = useState(() => toIsoDate(new Date()) ?? '');

  /*
    "Take booking" on the morning screen arrives as `takeBookingIntent` rather than a view.

    Checked against the permission here as well as at the card that offers it, which is the rule
    about never offering a write the server will reject: the Today card is gated on `RentalOrder:View`
    and creating needs `RentalOrder:Create`, so a person can hold the first without the second and
    the intent must not open a screen whose Save is going to 403.
  */
  const takingBooking = initialView === takeBookingIntent;

  // Where the board opens — a Today card names a view, which `boardStart` turns into a list and day.
  const [start] = useState(() => boardStart(takingBooking ? undefined : initialView, today));
  const [selectedId, setSelectedId] = useState<number | null>(initialBookingId ?? null);
  const [screen, setScreen] = useState<
    'detail' | 'issue' | 'return' | 'inspect' | 'settle' | 'amend' | 'extend'
  >('detail');
  const [creating, setCreating] = useState(takingBooking && canAct(session, 'RentalOrder:Create'));

  // The booking just taken, shown on the Booked page until the clerk opens it or takes another.
  const [justBooked, setJustBooked] = useState<{ booking: Booking; tokenFailed: boolean } | null>(
    null,
  );

  // Which overlay is open on top of the detail screen. Both are drawers rather than screens, because
  // neither replaces what you were looking at — taking a payment and sending a sleeve to the tailor
  // are things you do *to* the booking in front of you.
  // The amount rides with the kind only from the hand-over counter, which knows the GST to come.
  const [takingPayment, setTakingPayment] = useState<{ kind: PaymentKind; amount?: number } | null>(
    null,
  );

  // Whether the discount drawer is open. Its own slot rather than a value in `takingPayment`: a
  // concession is not a payment, and sharing the slot would let one close the other.
  const [discounting, setDiscounting] = useState(false);
  const [movingGarment, setMovingGarment] = useState<BookingGarment | null>(null);

  // The other party's hold, when the counter is chasing it off the picker. Held here rather than in
  // the picker because the drawer changes somebody else's booking, and this component owns every
  // mutation on this screen.
  const [chasing, setChasing] = useState<AvailableUnit | null>(null);

  // Which garment's own gallery is open. Separate from `movingGarment` because looking at a
  // picture and changing a state are different intents that must not share a slot.
  const [viewingGarment, setViewingGarment] = useState<BookingGarment | null>(null);

  // Which garment's timeline is open. Its own slot beside the gallery for the reason that one has
  // its own: looking at a picture and reading a history are different intents.
  const [garmentHistory, setGarmentHistory] = useState<BookingGarment | null>(null);

  // Which wearer is being booked in to be measured. Its own slot for the reason the two above have
  // theirs: a fitting is a fact about a person and the others are about a garment.
  const [fittingWearer, setFittingWearer] = useState<BookingWearer | null>(null);

  // A stage move that asks something first — a hold's hours, a reason, what a cancellation does
  // with the money (`StageDialog`, the phone's stage screen).
  const [staging, setStaging] = useState<StageMove | null>(null);

  // Refetched whole rather than picked out of the register's page: a booking's detail carries its
  // party, its garments and its payments, and the register deliberately does not page on those.
  const selected = useBooking(selectedId ?? undefined);
  const letterhead = useLetterhead(branchId);

  // Only fetched for an overdue booking — see the panel below for why the question is not worth
  // asking of every booking in progress.
  const cascade = useBookingCascade(selectedId ?? undefined, selected.data?.isOverdue === true);

  const currency = '₹';

  /**
   * The largest concession this user may give here (spec Section 4).
   *
   * <p>
   * <strong>From the acting branch, never from the user-level figure.</strong> `user.maxDiscountPct`
   * is the ceiling of the tenant-wide roles alone, so a manager whose authority is granted per shop
   * reads zero there while carrying a real figure at each branch — and the reverse case is the
   * dangerous one, where a manager at head office would otherwise be offered the manager's discount
   * at the till of a shop where they are a clerk.
   * </p>
   *
   * <p>
   * Advisory, like every capability sent to a client: it bounds the control and explains itself, and
   * the server resolves the same ceiling again on the way in. An existing booking carries its own
   * `discount.ceilingPct`, resolved for the same user against that booking's branch, which is what
   * the detail drawer reads instead of this.
   * </p>
   */
  const discountCeilingPct = canAct(session, 'RentalOrder:Discount')
    ? (session.branch?.maxDiscountPct ?? 0)
    : 0;

  /* ------------------------------------------------------------- the drawer */

  const [search, setSearch] = useState<{ from: string; to: string } | null>(null);

  /**
   * The rail's search, as a stable function.
   *
   * <p>
   * <strong>`useCallback` here rather than a suppression there.</strong> `NewBookingPage` now
   * searches from an effect when its possession dates change, and an inline arrow is a new function
   * on every render — so an honest dependency list would re-run that effect every render, set this
   * state, and render again. Wrapping it is what lets the effect list what it actually uses instead
   * of carrying a comment explaining why it does not.
   * </p>
   */
  const searchAvailability = useCallback(
    ({ from, to }: { from: string; to: string }) => setSearch({ from, to }),
    [],
  );

  /*
    The rail, in one call: every stocked design with all its garments over the possession dates
    (`GET /api/availability/catalogue`). It replaced a request per design, capped at sixty — the
    design cards need the design (photo, category) and the garment view needs the garments, and
    both now read this one answer, so the two views cannot disagree about what is free.
  */
  const availability = useCatalogue(branchId, {
    fromDate: search?.from,
    toDate: search?.to,
    pageSize: RailDesigns,
  });
  const designs = useMemo(() => availability.data?.designs ?? [], [availability.data]);
  const railTotal = availability.data?.total ?? 0;
  const moreDesigns =
    railTotal > designs.length
      ? `Showing the first ${designs.length} of ${railTotal} designs — the rail does not reach the rest yet.`
      : null;

  // Flattened into the garment view's own shape, which is deliberately narrower than the
  // availability result: the picker needs a garment, a price and what is in the way.
  const units: AvailableUnit[] = useMemo(() => railFromCatalogue(designs), [designs]);

  /*
     There is no rate-card query here any more.

     The screen used to fetch one card per design (`/api/rate-slab/resolve/{itemId}`) and price every
     garment of that design from it — which could not express two copies of one design costing
     different money, and that is now the ordinary case. The availability answer carries each
     garment's own card instead, resolved through the whole chain server-side, so the picker prices
     per garment and the counter makes one fewer round trip per design.
  */
  /**
   * Taking a booking, and then taking the token against it.
   *
   * <p>
   * <strong>Two calls, because they are two facts.</strong> `POST /api/booking` has no field for
   * money — deliberately, since a booking exists whether or not anything has been paid — so the
   * token the clerk agreed becomes a `TblRentalPayment` row of its own immediately afterwards. Until
   * now the draft computed a token, showed it on the confirm button, and then dropped it: the
   * booking was created and no payment was ever recorded.
   * </p>
   *
   * <p>
   * <strong>A failed token does not undo the booking</strong>, and that is the right way round. The
   * garments are locked and the customer has agreed; a payment that did not record is a receipt to
   * re-enter, whereas rolling the booking back would put the stock in front of the next caller. The
   * Booked page says so out loud — until it existed, a failed token was silent here.
   * </p>
   */
  const create = useApiMutation({
    mutationFn: async (
      draft: BookingDraft,
    ): Promise<{ booking: Booking; tokenFailed: boolean }> => {
      let booking = await createBooking(createBookingInput(draft));

      // The token, then the deposit where it is taken at booking — one press, two payments
      // (`paymentsAtBooking`), the phone's rule. One failing stops the rest; the Booked page says so.
      try {
        for (const payment of paymentsAtBooking(draft)) {
          booking = await recordBookingPayment(booking.id, payment);
        }

        return { booking, tokenFailed: false };
      } catch {
        return { booking, tokenFailed: true };
      }
    },
    // No toast: the Booked page is the confirmation, and it says "quotation" when it was one.
    invalidates: [bookingsKey, availabilityKey],
    onSuccess: (result) => {
      setCreating(false);
      setJustBooked(result);
    },
  });

  /* -------------------------------------------------------------- the money */

  // A receipt per account (canvas "Money in, by account"); a refusal part-way keeps the page open.
  const pay = useApiMutation({
    mutationFn: ({
      id,
      kind,
      notes,
      parts,
    }: {
      id: number;
      kind: PaymentKind;
      notes: string | null;
      parts: readonly SplitPart[];
    }) => recordBookingPaymentInParts(id, { kind, notes }, parts),
    success: ({ saved, failed }) =>
      failed
        ? `Part taken — ${saved.length} of ${saved.length + 1} parts went through.`
        : saved.length === 1
          ? 'Payment recorded.'
          : `${saved.length} payments recorded.`,
    invalidates: [bookingsKey, cashKey],
    onSuccess: ({ failed }) => {
      if (!failed) {
        setTakingPayment(null);
      }
    },
  });

  /**
   * Closing the deposit, which is not the same call as recording a refund.
   *
   * The server computes the deductions and writes both the refund receipt and the closing state.
   * Posting a `DepositRefund` payment instead puts the money on the ledger and leaves the deposit
   * `Held` — which is exactly what happened the first time this flow was driven end to end.
   */
  const settleDeposit = useApiMutation({
    // Netted against what is owed (the shared command asks for it), and what the deposit could not
    // cover taken in the same click when the clerk said so.
    mutationFn: async ({
      id,
      refundMethod,
      cashAccountId = null,
      collect = false,
    }: {
      id: number;
      refundMethod: PaymentMethod;
      /** The account the refund leaves, or — collecting — what is taken goes into. */
      cashAccountId?: number | null;
      collect?: boolean;
    }) =>
      collectAfterSettling(
        await settleBookingDeposit(id, {
          refundMethod,
          refundCashAccountId: collect ? null : cashAccountId,
        }),
        collect ? refundMethod : null,
        collect ? cashAccountId : null,
      ),
    success: 'Deposit settled.',
    invalidates: [bookingsKey],
    onSuccess: () => setScreen('detail'),
  });

  /**
   * The concession (spec Section 4), which is a write of its own and not a field on the save.
   *
   * The same call gives, changes and clears it — both figures null is a withdrawal, which the server
   * lets past the ceiling deliberately so a mistyped 20% does not need a manager to undo.
   */
  const discount = useApiMutation({
    mutationFn: ({ id, input }: { id: number; input: ApplyDiscountInput }) =>
      applyBookingDiscount(id, input),
    success: 'Discount updated.',
    invalidates: [bookingsKey],
    onSuccess: () => setDiscounting(false),
  });

  /**
   * The board's Mark ready: every garment that can take the tick, one after another, so a refusal
   * stops the rest rather than leaving half a booking moved behind an error — the phone's rule.
   */
  const markReady = useApiMutation({
    mutationFn: async (booking: Booking) => {
      for (const move of markReadyMoves(booking)) {
        await changeGarmentState(booking.id, move.garmentId, { state: move.state });
      }
    },
    success: 'Marked ready.',
    invalidates: [bookingsKey],
  });

  /** A row's button on the board: open the booking at its counter, or mark it ready in place. */
  const openFromBoard = (intent: BoardIntent, row: Booking) => {
    if (intent === 'markReady') {
      markReady.mutate(row);
      return;
    }

    setSelectedId(row.id);
    setScreen(intent === 'takeBack' ? 'return' : 'issue');
  };

  const moveGarment = useApiMutation({
    mutationFn: ({
      id,
      garmentId,
      input,
    }: {
      id: number;
      garmentId: number;
      input: ChangeGarmentStateInput;
    }) => changeGarmentState(id, garmentId, input),
    success: 'Garment updated.',
    invalidates: [bookingsKey],
    onSuccess: () => setMovingGarment(null),
  });

  const fitting = useApiMutation({
    mutationFn: ({
      id,
      wearerId,
      input,
    }: {
      id: number;
      wearerId: number;
      input: RecordFittingInput;
    }) => recordFitting(id, wearerId, input),
    success: 'Fitting updated.',
    invalidates: [bookingsKey],
    onSuccess: () => setFittingWearer(null),
  });

  /* ---------------------------------------------------------- the lifecycle */

  const stage = useApiMutation({
    mutationFn: ({ id, input }: { id: number; input: ChangeBookingStageInput }) =>
      // Built by `stageMoveInput`, so a hold carries its hours and a lapse its fixed reason.
      changeBookingStage(id, input),
    success: 'Booking updated.',
    invalidates: [bookingsKey],
    onSuccess: () => setStaging(null),
  });

  /**
   * Handing over, and the money with it (the phone's one button, 2026-10-22). **Issuing never takes
   * money**: the hand-over is sent, then each payment through the ordinary payment, the amounts read
   * off the booking the hand-over answered with — so the GST taken is the invoice's own — out of the
   * account boxes, the rental first, then the deposit, a payment per account (split payments). A
   * payment that fails leaves the hand-over done and says so (what recorded stays); it is not undone,
   * because the customer has walked out with the garments.
   */
  const issue = useApiMutation({
    mutationFn: async ({
      id,
      garmentIds,
      acknowledgeMissingPhotos,
      accessoryIds,
      parts,
    }: {
      id: number;
      garmentIds: number[];
      acknowledgeMissingPhotos: boolean;
      accessoryIds: number[];
      /** The account boxes, or null to take nothing now. */
      parts: readonly SplitPart[] | null;
    }): Promise<{ paymentFailed: boolean }> => {
      // Passed through from the counter's own checkbox rather than hardcoded to true: §9 calls
      // issue-and-return photographs the primary dispute defence.
      const issued = await issueBooking(id, { garmentIds, acknowledgeMissingPhotos, accessoryIds });

      if (parts === null) {
        return { paymentFailed: false };
      }

      try {
        for (const payment of paymentsAfterIssueInParts(issued, parts)) {
          await recordBookingPayment(id, payment);
        }

        return { paymentFailed: false };
      } catch {
        return { paymentFailed: true };
      }
    },
    success: ({ paymentFailed }) =>
      paymentFailed
        ? 'Handed over — but not all of the payment recorded. Take the rest from the booking.'
        : 'Handed over.',
    invalidates: [bookingsKey],
    onSuccess: () => setScreen('detail'),
  });

  const takeBack = useApiMutation({
    mutationFn: async ({
      id,
      garmentIds,
      refundPoint,
      lateFee,
      accessoryIds,
      refundMethod,
      cashAccountId = null,
      collect = false,
    }: {
      id: number;
      garmentIds: number[];
      refundPoint: DepositRefundPoint;
      lateFee: number | null;
      accessoryIds: number[];
      refundMethod: PaymentMethod;
      /** The account the refund leaves, or — collecting — what is taken goes into. */
      cashAccountId?: number | null;
      collect?: boolean;
    }) =>
      collectAfterSettling(
        await returnBooking(id, {
          garmentIds,
          refundPoint,
          lateFee,
          accessoryIds,
          refundMethod,
          refundCashAccountId: collect ? null : cashAccountId,
        }),
        collect ? refundMethod : null,
        collect ? cashAccountId : null,
      ),
    success: 'Garments booked in.',
    invalidates: [bookingsKey],
    // On to checking what came back, unless the deposit was settled at the counter or this person
    // may not record inspections — the phone's walk (2026-10-22).
    onSuccess: (returned) => setScreen(afterTakeBack(returned)),
  });

  const checksGarments =
    hasFeature(session, 'ReturnInspection') && canAct(session, 'ReturnInspection:Edit');
  const settlesDeposits = can(session, 'RentalOrder:ViewFinancials');

  /** Where taking back leads: checking the garments, settling the deposit, or the booking. */
  const afterTakeBack = (returned: Booking): typeof screen =>
    checksGarments && hasGarmentsToCheck(returned)
      ? 'inspect'
      : settlesDeposits &&
          returned.deposit.state === 'Held' &&
          returned.deposit.refundPoint === 'AfterInspection'
        ? 'settle'
        : 'detail';

  /** One garment's inspection — the definition both clients send (`@core/api/inspectionCommands`). */
  const inspect = useApiMutation({
    mutationFn: ({
      rentalOrderItemId,
      payload,
    }: {
      rentalOrderItemId: number;
      payload: ReturnType<typeof toRequest>;
    }) => saveInspection(rentalOrderItemId, payload),
    success: 'Inspection recorded.',
    // The booking carries the damage charge this writes.
    invalidates: [pendingInspectionsKey, inspectionsKey, bookingsKey],
  });

  /**
   * Changing what the booking is for — one call carrying every operation.
   *
   * <p>
   * <strong>Both keys are invalidated, and the second is the one that is easy to miss.</strong> A
   * dropped garment is free on the rail the instant this returns, so the availability query has to
   * go too — `bookingsKey` alone would leave the picker still showing it as spoken for by this very
   * booking. `HoldDrawer` already relies on the same prefix behaviour for the same reason.
   * </p>
   */
  const amend = useApiMutation({
    mutationFn: ({ id, amendment }: { id: number; amendment: Amendment }) =>
      amendBooking(id, amendmentRequest(amendment)),
    success: 'Booking amended.',
    invalidates: [bookingsKey, availabilityKey],
    onSuccess: () => setScreen('detail'),
  });

  /**
   * More days on a booking already out (Section 8, item 2.6).
   *
   * <p>
   * The availability key goes with the booking key for the reason the amendment's does: the blocks
   * have moved, so a rail the clerk was just looking at is now wrong about which days this garment
   * is free.
   * </p>
   */
  const extend = useApiMutation({
    mutationFn: ({ id, input }: { id: number; input: ExtendBookingInput }) =>
      extendBooking(id, input),
    success: 'Extension granted.',
    invalidates: [bookingsKey, availabilityKey],
    onSuccess: () => setScreen('detail'),
  });

  /* -------------------------------------------------------------- rendering */

  const booking = selected.data;

  if (justBooked) {
    return (
      <BookedPage
        booking={justBooked.booking}
        tokenFailed={justBooked.tokenFailed}
        letterhead={letterhead.data}
        currency={currency}
        onOpen={() => {
          setSelectedId(justBooked.booking.id);
          setScreen('detail');
          setJustBooked(null);
        }}
        onAnother={() => {
          setJustBooked(null);
          setCreating(true);
        }}
      />
    );
  }

  if (booking && screen === 'issue') {
    return (
      <>
        <IssueCounter
          booking={booking}
          currency={currency}
          photoRequired={bookingDefaultsOf(session.user).issueRequiresPhoto === true}
          busy={issue.isPending}
          onCancel={() => setScreen('detail')}
          mayTakeMoney={can(session, 'RentalOrder:ViewFinancials')}
          onIssue={(garmentIds, acknowledgeMissingPhotos, accessoryIds, parts) =>
            issue.mutate({
              id: booking.id,
              garmentIds,
              acknowledgeMissingPhotos,
              accessoryIds,
              parts,
            })
          }
        />
      </>
    );
  }

  if (booking && screen === 'extend') {
    return (
      <ExtendBookingPage
        booking={booking}
        busy={extend.isPending}
        onCancel={() => setScreen('detail')}
        onSave={(input) => extend.mutate({ id: booking.id, input })}
      />
    );
  }

  if (booking && screen === 'amend') {
    return (
      <>
        <AmendBookingPage
          booking={booking}
          units={units}
          designs={designs}
          moreDesigns={moreDesigns}
          searching={availability.isFetching}
          searched={search !== null}
          currency={currency}
          busy={amend.isPending}
          // The dates are the booking's and do not move — an amendment changes *what*, never
          // *when*. Extension is its own operation and its own table, deliberately not folded in.
          onSearch={() =>
            setSearch({ from: booking.possessionFromDate, to: booking.possessionToDate })
          }
          onClose={() => setScreen('detail')}
          onApply={(amendment) => amend.mutate({ id: booking.id, amendment })}
          onChaseHold={setChasing}
        />

        {chasing?.heldBy?.holder && (
          <HoldDrawer
            serialCode={chasing.serialCode}
            variantDisplayName={chasing.variantDisplayName}
            holder={chasing.heldBy.holder}
            fromDate={chasing.heldBy.fromDate}
            toDate={chasing.heldBy.untilDate}
            session={session}
            onClose={() => setChasing(null)}
          />
        )}
      </>
    );
  }

  if (booking && screen === 'inspect') {
    return (
      <CheckGarments
        booking={booking}
        saving={inspect.isPending}
        onSave={(rentalOrderItemId, payload) => inspect.mutateAsync({ rentalOrderItemId, payload })}
        onDone={() =>
          setScreen(
            settlesDeposits &&
              booking.deposit.state === 'Held' &&
              booking.deposit.refundPoint === 'AfterInspection'
              ? 'settle'
              : 'detail',
          )
        }
      />
    );
  }

  if (booking && screen === 'settle') {
    return (
      <SettlePage
        booking={booking}
        currency={currency}
        busy={settleDeposit.isPending}
        onSettle={(refundMethod, collect, cashAccountId) =>
          settleDeposit.mutate({ id: booking.id, refundMethod, collect, cashAccountId })
        }
        onBack={() => setScreen('detail')}
      />
    );
  }

  if (booking && screen === 'return') {
    return (
      <ReturnCounter
        booking={booking}
        currency={currency}
        busy={takeBack.isPending}
        onCancel={() => setScreen('detail')}
        onSettle={({
          garmentIds,
          refundPoint,
          lateFee,
          accessoryIds,
          refundMethod,
          cashAccountId,
          collect,
        }) =>
          takeBack.mutate({
            id: booking.id,
            garmentIds,
            refundPoint,
            lateFee,
            accessoryIds,
            refundMethod,
            cashAccountId,
            collect,
          })
        }
      />
    );
  }

  if (booking && takingPayment !== null) {
    return (
      <PaymentPage
        booking={booking}
        currency={currency}
        branchId={branchId}
        busy={pay.isPending}
        initialKind={takingPayment.kind}
        initialAmount={takingPayment.amount}
        onClose={() => setTakingPayment(null)}
        onRecord={(kind, notes, parts) => pay.mutateAsync({ id: booking.id, kind, notes, parts })}
      />
    );
  }

  if (booking) {
    // The one thing that happens next, from the rule the phone reads too. It leads the page, and the
    // row of actions at the foot stops repeating it.
    const step = nextStep(
      booking,
      {
        act: canAct(session, 'RentalOrder:Edit'),
        money: can(session, 'RentalOrder:ViewFinancials'),
      },
      { currency },
    );
    const stepBusy = stage.isPending || settleDeposit.isPending;

    return (
      <>
        <BookingDetail
          next={
            step && (
              <NextStepCard step={step}>
                {step.kind === 'confirm' && (
                  <Button
                    variant="primary"
                    disabled={stepBusy}
                    onClick={() =>
                      stage.mutate({ id: booking.id, input: stageMoveInput(step.move) })
                    }
                  >
                    {step.label}
                  </Button>
                )}
                {step.kind === 'handOver' && (
                  <Button variant="primary" onClick={() => setScreen('issue')}>
                    {step.label}
                  </Button>
                )}
                {step.kind === 'takeBack' && (
                  <Button variant="primary" onClick={() => setScreen('return')}>
                    {step.label}
                  </Button>
                )}
                {step.kind === 'payment' && (
                  <Button
                    variant="primary"
                    onClick={() => setTakingPayment({ kind: step.paymentKind })}
                  >
                    {step.label}
                  </Button>
                )}
                {step.kind === 'settle' && (
                  <Button variant="primary" onClick={() => setScreen('settle')}>
                    {step.label}
                  </Button>
                )}
              </NextStepCard>
            )
          }
          booking={booking}
          letterhead={letterhead.data}
          currency={currency}
          today={today}
          prints={can(session, 'RentalOrder:Print')}
          readsInvoices={hasFeature(session, 'Billing') && can(session, 'SalesInvoice:View')}
          notice={
            // Section 7's cascade, asked only once the booking is actually late — a panel saying
            // "nothing" on every booking in progress teaches people to stop reading it.
            booking.isOverdue && (
              <CascadeNotice cascade={cascade.data} loading={cascade.isLoading} />
            )
          }
          actions={
            <BookingActions
              session={session}
              booking={booking}
              step={step}
              busy={stage.isPending || settleDeposit.isPending}
              onStage={(move) =>
                move.to === 'Hold' || move.asksReason
                  ? setStaging(move)
                  : stage.mutate({ id: booking.id, input: stageMoveInput(move) })
              }
              onIssue={() => setScreen('issue')}
              onReturn={() => setScreen('return')}
              onAmend={() => setScreen('amend')}
              onTakePayment={(kind) => setTakingPayment({ kind })}
              onSettleDeposit={() => setScreen('settle')}
              onCheckGarments={
                checksGarments && hasGarmentsToCheck(booking)
                  ? () => setScreen('inspect')
                  : undefined
              }
              onDiscount={() => setDiscounting(true)}
            />
          }
          exits={
            canAct(session, 'RentalOrder:Edit')
              ? stageMoves(booking).filter((move) => move.kind === 'exit')
              : []
          }
          mayExtend={canAct(session, 'RentalOrder:Edit') && canExtend(booking)}
          onExit={setStaging}
          onExtend={() => setScreen('extend')}
          onBack={() => setSelectedId(null)}
          onMoveGarment={setMovingGarment}
          onViewPhotos={setViewingGarment}
          onShowHistory={setGarmentHistory}
          onFitting={setFittingWearer}
        />

        {staging && (
          <StageDialog
            booking={booking}
            move={staging}
            currency={currency}
            busy={stage.isPending}
            onSend={(input) => stage.mutate({ id: booking.id, input })}
            onClose={() => setStaging(null)}
          />
        )}

        {discounting && (
          <DiscountDrawer
            booking={booking}
            currency={currency}
            busy={discount.isPending}
            onClose={() => setDiscounting(false)}
            onApply={(input) => discount.mutate({ id: booking.id, input })}
          />
        )}

        {viewingGarment && (
          <GarmentPhotos garment={viewingGarment} onClose={() => setViewingGarment(null)} />
        )}

        {garmentHistory && (
          <GarmentTimeline
            unitId={garmentHistory.unitId}
            serialCode={garmentHistory.serialCode}
            subtitle={`${garmentHistory.itemName} · ${garmentHistory.variantDisplayName}`}
            onClose={() => setGarmentHistory(null)}
          />
        )}

        {fittingWearer && (
          <FittingDrawer
            wearer={fittingWearer}
            branchId={booking.branchId}
            busy={fitting.isPending}
            onClose={() => setFittingWearer(null)}
            onRecord={(input) =>
              fitting.mutate({ id: booking.id, wearerId: fittingWearer.id, input })
            }
          />
        )}

        {movingGarment && (
          <GarmentStateDrawer
            garment={movingGarment}
            branchId={booking.branchId}
            busy={moveGarment.isPending}
            onClose={() => setMovingGarment(null)}
            onMove={(input) =>
              moveGarment.mutate({ id: booking.id, garmentId: movingGarment.id, input })
            }
          />
        )}
      </>
    );
  }

  if (creating) {
    return (
      <>
        <NewBookingPage
          today={today}
          branchId={branchId}
          units={units}
          designs={designs}
          moreDesigns={moreDesigns}
          searching={availability.isFetching}
          searched={search !== null}
          currency={currency}
          discountCeilingPct={discountCeilingPct}
          bookingDefaults={bookingDefaultsOf(session.user)}
          busy={create.isPending}
          onSearch={searchAvailability}
          onClose={() => setCreating(false)}
          // A refusal re-reads the rail: if another counter took a garment meanwhile, the bag
          // names it and offers the next free one, rather than the clerk retrying the same refusal.
          onCreate={(draft) => create.mutate(draft, { onError: () => void availability.refetch() })}
          onChaseHold={setChasing}
        />

        {chasing?.heldBy?.holder && (
          <HoldDrawer
            serialCode={chasing.serialCode}
            variantDisplayName={chasing.variantDisplayName}
            holder={chasing.heldBy.holder}
            fromDate={chasing.heldBy.fromDate}
            toDate={chasing.heldBy.untilDate}
            session={session}
            // Releasing invalidates the availability key, which is a prefix of this picker's own
            // query — so the garment reappears as free without the clerk searching again.
            onClose={() => setChasing(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeading
        title="Bookings"
        subtitle="Take a booking, hand garments over, and book them back in."
        actions={
          <WriteAction session={session} required="RentalOrder:Create">
            {(disabled) => (
              <Button
                variant="primary"
                shortcut="create"
                disabled={disabled}
                onClick={() => setCreating(true)}
              >
                New booking
              </Button>
            )}
          </WriteAction>
        }
      />

      <BookingBoard
        branchId={branchId}
        today={today}
        currency={currency}
        start={start}
        initialSearch={initialFind}
        mayAct={canAct(session, 'RentalOrder:Edit')}
        busyId={markReady.isPending ? (markReady.variables?.id ?? null) : null}
        onOpen={(row) => {
          setSelectedId(row.id);
          setScreen('detail');
        }}
        onIntent={openFromBoard}
      />
    </>
  );
}

/**
 * How many designs the rail carries — the catalogue endpoint's own ceiling (it was sixty, silently,
 * when the rail asked once per design). A shop with more is told so under the cards; reaching the
 * rest wants the search sent to the server, which is a change to the rail's contract.
 */
const RailDesigns = 100;

/**
 * The moves available from the detail screen.
 *
 * <p>
 * Separate from `BookingDetail`, which is deliberately a read-only rendering of a booking and its
 * two printed documents. Keeping the buttons out here means the detail component stays testable
 * without a session, a permission set or a mutation in sight.
 * </p>
 *
 * <p>
 * <strong>Only the moves that are legal from here are offered.</strong> The server refuses the rest
 * and says why, but a button that exists only to be rejected is a button that teaches a clerk to
 * distrust the screen.
 * </p>
 */
function BookingActions({
  session,
  booking,
  step,
  busy,
  onStage,
  onIssue,
  onReturn,
  onAmend,
  onTakePayment,
  onSettleDeposit,
  onCheckGarments,
  onDiscount,
}: {
  session: Session;
  booking: Booking;
  /** The step the page leads with — not offered a second time down here. */
  step: NextStep | null;
  busy: boolean;
  /** A stage move — sent straight away, or through its dialog where it asks something. */
  onStage: (move: StageMove) => void;
  onIssue: () => void;
  onReturn: () => void;
  onAmend: () => void;
  onTakePayment: (kind: PaymentKind) => void;
  onSettleDeposit: () => void;
  /** Offered while garments are back and not yet checked — a return checked later. */
  onCheckGarments?: (() => void) | undefined;
  onDiscount: () => void;
}) {
  // The legal moves, from the rule the phone uses too. Cancelling is pre-issue only — once a garment
  // has left the shop the way out is Recovery, not cancellation.
  const moves = stageMoves(booking);
  const forward = moves.filter(
    (move) => move.kind === 'forward' && !(step?.kind === 'confirm' && step.move.to === move.to),
  );

  // Its own grant, checked apart from the `RentalOrder:Edit` this row sits behind — arranging
  // garments and giving money away are different authorities. Shared with the phone.
  const mayDiscount = canDiscount(session, booking);

  const owes =
    booking.balanceDue > 0 && !(step?.kind === 'payment' && step.paymentKind === 'RentalBalance');
  const depositDue =
    booking.deposit.state === 'NotTaken' &&
    (booking.deposit.amount ?? 0) > 0 &&
    !(step?.kind === 'payment' && step.paymentKind === 'Deposit');
  const depositHeld = booking.deposit.state === 'Held' && step?.kind !== 'settle';

  const anyMoney = mayDiscount || owes || depositDue || depositHeld;

  return (
    <WriteAction session={session} required="RentalOrder:Edit">
      {(blocked) => {
        const off = blocked || busy;

        return (
          /*
            Three groups, not one row of nine.

            `flex`, not Bootstrap's `d-flex` — this file had carried the latter since the port, so
            the buttons had been stacking rather than sitting in a row. Fixing that produced the
            opposite problem: **Cancel booking and Escalate to recovery sat flush against Issue
            garments**, one gap away from the button a clerk presses forty times a day. So the moves
            are grouped by what they do — where the booking stands, then its money, then the ways
            out — and the last group is ruled off. Nothing here changes which buttons appear.
          */
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {/* Quote, hold and confirm, from the shared rule (`@core/bookings/stageMoves`) — which
                is also what puts the hours on a hold. The Hold button here sent none until
                2026-10-06, and the server refuses every hold without them. */}
              {/* All quiet: the step above is the one primary button on the page. */}
              {forward.map((move) => (
                <Button key={move.to} onClick={() => onStage(move)} disabled={off}>
                  {move.label}
                </Button>
              ))}

              {booking.stage === 'Confirmed' && step?.kind !== 'handOver' && (
                <Button icon="icon icon-package-check" onClick={onIssue} disabled={off}>
                  Issue garments
                </Button>
              )}

              {onCheckGarments && (
                <Button icon="icon icon-badge-check" onClick={onCheckGarments} disabled={off}>
                  Check garments
                </Button>
              )}

              {booking.stage === 'InProgress' && step?.kind !== 'takeBack' && (
                <Button icon="icon icon-package-open" onClick={onReturn} disabled={off}>
                  Book garments in
                </Button>
              )}

              {/* Changing what the booking is for, rather than where it stands.
                Offered on every stage the server will accept — including `InProgress`, which is the
                pickup-day case the whole feature exists for: the groom's jacket does not fit while
                his brother's suit is already issued. Before this the shop's only option was to
                cancel and rebook, which loses the BK number, the payment ledger, the wearer
                structure and the condition photographs — and is impossible once anything is out. */}
              {canAmend(booking) && (
                <Button icon="icon icon-replace" onClick={onAmend} disabled={off}>
                  Amend garments
                </Button>
              )}
            </div>

            {anyMoney && (
              <div className="flex flex-wrap gap-2">
                {/* The money track, which had no buttons at all. Disabled on `busy` only and not on
                `blocked`: the outer guard asks about `RentalOrder:Edit`, and this button answers to
                `RentalOrder:Discount` instead. */}
                {mayDiscount && (
                  <Button icon="icon icon-percent" onClick={onDiscount} disabled={busy}>
                    {booking.discount.amount ? 'Change discount' : 'Give discount'}
                  </Button>
                )}

                {owes && (
                  <Button
                    icon="icon icon-indian-rupee"
                    onClick={() => onTakePayment('RentalBalance')}
                    disabled={off}
                  >
                    Take payment
                  </Button>
                )}

                {depositDue && (
                  <Button
                    icon="icon icon-shield"
                    onClick={() => onTakePayment('Deposit')}
                    disabled={off}
                  >
                    Take deposit
                  </Button>
                )}

                {/* Settling outside the return counter is what makes `AfterInspection` and
                `AfterCleaning` reachable at all — on those two settings the return screen
                deliberately does not close the deposit, so it stayed Held forever.

                This calls settlement, *not* the payment endpoint. Recording a `DepositRefund`
                payment puts the money on the ledger and leaves the deposit open, because only
                settlement computes the deductions and writes the closing state. That distinction
                was found by driving the flow end to end, not by reading the code. */}
                {/* The method is asked before the call rather than assumed, because this writes the
                refund row and the server took cash on faith until 2026-09-09. A `Popconfirm` and
                not a drawer: it is one question anchored to the button that raised it, which is the
                confirm-dialog case the insert/update rule leaves alone. */}
                {depositHeld && (
                  <Button icon="icon icon-shield-check" onClick={onSettleDeposit} disabled={off}>
                    Settle deposit
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      }}
    </WriteAction>
  );
}

/** Garments back and not yet looked at — what "Check each garment" walks through. */
function hasGarmentsToCheck(booking: Booking): boolean {
  return booking.wearers.some((wearer) =>
    wearer.garments.some((garment) => garment.state === 'Returned'),
  );
}
